import React from 'react';

import {
    Grid,
    Button,
    Divider,
    Collapse,
    IconButton
} from '@mui/material';
import {
    CheckBox,
    CheckBoxOutlineBlank,
    ExpandMore,
    ExpandLess,
    Add,
    CardMembership,
    Remove
} from '@mui/icons-material';

import { BoolField, TextField } from '@commonComponents/Form';
import { SectionDivider } from '@commonComponents';
import { TenantProfileType, METADATA_TYPES } from '@types';
import { hasStandardProperty, isCustomCertAlreadyInList, isCustomCertInPresets } from '@utils/metadata';

interface LicencesCertificatesProps {
    addCustomLicenceCertificate: () => void;
    customLicencesCertificates: Array<{ id: string; value: string }>;
    licencesCertificates: TenantProfileType['licencesCertificates'];
    onAllNone: ({ key, array }: { key: string; array: string[] }) => void;
    onChangeCustomLicenceCertificate: ({ id, newValue }: { id: string; newValue: string }) => void;
    onClick: ({ key, value }: { key: string; value: string }) => void;
    onRemoveCustomLicenceCertificate: (certId: string) => void;
    settables: string[];
}

const LicencesCertificates: React.FC<LicencesCertificatesProps> = (props) => {
    const {
        addCustomLicenceCertificate,
        customLicencesCertificates = [],
        licencesCertificates = [],
        onAllNone,
        onChangeCustomLicenceCertificate,
        onClick,
        onRemoveCustomLicenceCertificate,
        settables = []
    } = props;

    const [collapsed, setCollapsed] = React.useState<boolean>(false);
    const [selected, setSelected] = React.useState<'all' | 'some' | 'none'>('some');
    const key = METADATA_TYPES.LICENCES_CERTIFICATES;

    const licenseCount = (licencesCertificates?.filter((lic: string) => settables.includes(lic)).length || 0)
        + customLicencesCertificates.filter((lic) => lic.value !== '').length;

    React.useEffect(() => {
        if (settables.every((cert) => licencesCertificates.includes(cert))) {
            setSelected('all');
        } else if (licencesCertificates.length === 0) {
            setSelected('none');
        } else {
            setSelected('some');
        }
    }, [licencesCertificates, settables]);

    const selectAll = () => {
        const newSus = Array.from(new Set([...licencesCertificates, ...settables]));
        onAllNone({ key, array: newSus });
    };

    const selectNone = () => {
        const newSus = licencesCertificates.filter((sus: string) => !settables.includes(sus));
        onAllNone({ key, array: newSus });
    };

    return (
        <Grid item xs={12} lg={6}>
            <SectionDivider
                label={(
                    <>
                        {`Licenser och certifikat  (${licenseCount}) `}
                        {collapsed ? <ExpandMore /> : <ExpandLess />}
                    </>
                )}
                icon={<CardMembership fontSize="small" />}
                marginTop={0}
                marginBottom={2}
                onClick={() => setCollapsed(!collapsed)}
            />
            <Collapse in={!collapsed}>
                <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item>
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => selectAll()}
                            startIcon={selected === 'all' ? <CheckBox /> : <CheckBoxOutlineBlank />}
                            sx={{ fontWeight: 300 }}
                        >
                            Alla
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => selectNone()}
                            startIcon={selected === 'none' ? <CheckBox /> : <CheckBoxOutlineBlank />}
                            sx={{ fontWeight: 300 }}
                        >
                            Inga
                        </Button>
                    </Grid>
                </Grid>
                <Divider sx={{ mb: 2, width: '160px' }} />
                <Grid container spacing={1}>
                    {settables.map((cert) => (
                        <Grid item xs={12} sm={6} md={4} key={cert}>
                            <BoolField
                                name={cert}
                                label={cert}
                                value={hasStandardProperty({
                                    array: licencesCertificates,
                                    value: cert
                                })}
                                onChange={() => onClick({ key, value: cert })}
                                sx={{ py: 0.25 }}
                            />
                        </Grid>
                    ))}
                    {customLicencesCertificates.map((cert) => (
                        <Grid item xs={12} key={cert.id} container spacing={1} alignItems="center">
                            <Grid item xs>
                                <TextField
                                    name={`custom-cert-${cert.id}`}
                                    label="Egen licens/certifikat"
                                    value={cert.value}
                                    onChange={(e: unknown) => {
                                        const newValue = (
                                            typeof e === 'object'
                                            && e !== null
                                            && 'target' in e
                                            && typeof (e as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)
                                                .target?.value === 'string'
                                        )
                                            ? (e as React.ChangeEvent<
                                                HTMLInputElement | HTMLTextAreaElement
                                            >).target.value
                                            : ((e as Record<string, string | null
                                                | undefined>)[`custom-cert-${cert.id}`]
                                                ?? '');
                                        onChangeCustomLicenceCertificate({
                                            id: cert.id,
                                            newValue
                                        });
                                    }}
                                    required
                                    customValidations={
                                        [
                                            {
                                                isValid: !isCustomCertAlreadyInList({
                                                    certId: cert.id,
                                                    value: cert.value,
                                                    certs: customLicencesCertificates
                                                }),
                                                errorText: 'Detta certifikat finns redan'
                                            },
                                            {
                                                isValid: !isCustomCertInPresets({
                                                    value: cert.value,
                                                    presets: settables
                                                }),
                                                errorText: 'Detta certifikat finns som förinställt alternativ.'
                                            }
                                        ]
                                    }
                                    disabled={cert.id.startsWith('init-')}
                                />
                            </Grid>
                            <Grid item>
                                <IconButton
                                    onClick={() => onRemoveCustomLicenceCertificate(cert.id)}
                                    color="error"
                                    size="small"
                                >
                                    <Remove />
                                </IconButton>
                            </Grid>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <IconButton
                            onClick={() => addCustomLicenceCertificate()}
                            color="primary"
                            size="small"
                        >
                            <Add />
                        </IconButton>
                    </Grid>
                </Grid>
            </Collapse>
        </Grid>
    );
};

export default LicencesCertificates;
